import { useCallback, useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import type { User } from "@supabase/supabase-js";
import * as orderService from "@/supabase/order/service/orderService";
import { logError } from "@/lib/sentry";
import { toast } from "sonner";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

export type ShippingMode = 'auto' | 'jeju' | 'free' | `preset:${number}`;

interface UseShippingFeeModeProps {
  user: User | null;
  activeCustomer: CustomerTemplate | null | undefined;
  activeCustomerId: string | null;
  jejuShippingFee: number;
  shippingPresets: number[];
  guestShippingOverrides: Record<string, number | null>;
  setGuestShippingOverrides: Dispatch<SetStateAction<Record<string, number | null>>>;
  setShippingFeeOverride: (value: number | null) => void;
  extractOrderId: (customerId: string) => number | null;
  // 배송 override 저장 성공(DB 커밋) 후 status reconcile — 자동 전환 반영 (스펙 §16.10)
  onOrderMutated?: (orderId: number) => void;
}

interface UseShippingFeeModeResult {
  shippingMode: ShippingMode;
  handleShippingModeChange: (mode: ShippingMode) => void;
  setShippingFeeOverridePersisted: (value: number | null) => void;
}

/**
 * 배송비 모드(auto | jeju | free) 도메인 훅.
 * override source(로그인=shippingFeeOverride, 비로그인=guestShippingOverrides)에서 모드를 derive하고,
 * 모드 변경 / 직접 override 설정 시 로컬·DB·localStorage를 동기화한다.
 *
 * guestShippingOverrides는 Home이 소유(usePurchaseState·shippingMode·JSX 공유 hub) → live 참조로 주입(snapshot 금지).
 * setShippingFeeOverride·jejuShippingFee는 usePurchaseState 산출물 → 본 훅은 usePurchaseState 이후 호출해야 한다.
 * localStorage restore→save 순서를 보존(restore effect를 save effect보다 먼저 선언).
 */
export function useShippingFeeMode({
  user,
  activeCustomer,
  activeCustomerId,
  jejuShippingFee,
  shippingPresets,
  guestShippingOverrides,
  setGuestShippingOverrides,
  setShippingFeeOverride,
  extractOrderId,
  onOrderMutated,
}: UseShippingFeeModeProps): UseShippingFeeModeResult {
  // 주문별 DB 쓰기 직렬화 큐(§16.10) — 연타 시 fire-and-forget 요청들의 커밋 순서가 클릭 순서와
  // 어긋나면 로컬 UI(마지막 클릭)와 DB(마지막 커밋)가 갈라진다 → 이전 쓰기 완료 후 다음 쓰기.
  // 각 쓰기 커밋 "뒤에" reconcile — 자동 전환 트리거는 이 UPDATE와 원자적이라 완료 시점에만
  // 새 status가 관측된다. 실패는 토스트로 표면화 + 체인 계속(다음 클릭 값이 최종 수렴) +
  // status reconcile(커밋됐는데 응답만 유실된 모호 실패에서 자동 전환 반영 — 로컬 override 표시
  // 되돌림은 진행 중 클릭과 경합해 미실시, 기존 fire-and-forget 동작 유지·토스트가 새로고침 유도).
  const overrideWriteQueueRef = useRef(new Map<number, Promise<void>>());
  const persistOverrideToDb = useCallback((orderId: number, value: number | null) => {
    const queue = overrideWriteQueueRef.current;
    const tail = queue.get(orderId) ?? Promise.resolve();
    const next = tail
      .then(() => orderService.updateShippingFeeOverride(orderId, value))
      .then(() => { onOrderMutated?.(orderId); })
      .catch(err => {
        logError('shipping_fee_override 업데이트 실패', err);
        toast.error('배송비 저장에 실패했습니다.\n새로고침 후 다시 확인합니다.');
        onOrderMutated?.(orderId);
      });
    queue.set(orderId, next);
    // 정착한 tail만 정리(주문당 1엔트리 누수 방지) — 더 새 tail이 이미 들어섰으면 건드리지 않음.
    void next.finally(() => {
      if (queue.get(orderId) === next) queue.delete(orderId);
    });
  }, [onOrderMutated]);

  // 배송 모드: auto | jeju | free — override source에서 직접 derive.
  // (이전: 별도 state + activeCustomer 변경 effect로 미러 → 클릭당 추가 렌더 발생.)
  // override source(로그인=shippingFeeOverride, 비로그인=guestShippingOverrides)는 핸들러가 갱신하므로 동작 동등.
  const shippingMode = useMemo<ShippingMode>(() => {
    const override = activeCustomer
      ? (user
        ? (activeCustomer as CustomerTemplate).shippingFeeOverride ?? null
        : (activeCustomerId ? guestShippingOverrides[activeCustomerId] ?? null : null))
      : null;
    if (override === 0) return 'free';
    // 프리셋을 제주보다 먼저 검사 — 커밋 상태에선 validation이 둘을 disjoint로 보장해 순서 무영향,
    // 미저장 제주 '드래프트'가 커밋 프리셋과 같은 값일 때만 커밋된 진실(프리셋 표시)이 이긴다(codexloop R2).
    if (override != null && shippingPresets.includes(override)) return `preset:${override}`;
    if (override === jejuShippingFee) return 'jeju';
    return 'auto';
  }, [user, activeCustomer, activeCustomerId, guestShippingOverrides, jejuShippingFee, shippingPresets]);

  const handleShippingModeChange = useCallback((mode: ShippingMode) => {
    if (!activeCustomerId) return;
    // 로컬 override 반영
    let overrideValue: number | null = null;
    if (mode === 'auto') {
      overrideValue = null;
    } else if (mode === 'jeju') {
      overrideValue = jejuShippingFee;
    } else if (mode === 'free') {
      overrideValue = 0;
    } else {
      // 프리셋: mode = `preset:${amount}` — 양의 정수만 허용
      const amount = Number(mode.slice('preset:'.length));
      if (!Number.isInteger(amount) || amount <= 0) return;
      overrideValue = amount;
    }
    // 회원: 기존 로컬 상태 업데이트 사용
    // 비회원: guestOverrides에 저장
    if (user) {
      setShippingFeeOverride(overrideValue);
    } else if (activeCustomerId) {
      setGuestShippingOverrides(prev => ({ ...prev, [activeCustomerId]: overrideValue }));
      setShippingFeeOverride(overrideValue);
    }

    // 로그인 사용자면 DB에도 즉시 반영(주문별 직렬화 큐 경유 — 위 persistOverrideToDb 주석 참조).
    if (user && activeCustomerId.startsWith('order_')) {
      const orderId = extractOrderId(activeCustomerId);
      if (orderId) {
        persistOverrideToDb(orderId, overrideValue);
      }
    }
  }, [user, activeCustomerId, extractOrderId, setShippingFeeOverride, setGuestShippingOverrides, jejuShippingFee, persistOverrideToDb]);

  // 패널/기타에서 직접 override를 설정할 때 DB까지 반영하는 헬퍼
  const setShippingFeeOverridePersisted = useCallback((value: number | null) => {
    if (!activeCustomerId) return;
    if (user) {
      setShippingFeeOverride(value);
    } else {
      // 비회원: guestOverrides에 저장
      setGuestShippingOverrides(prev => ({ ...prev, [activeCustomerId]: value }));
      setShippingFeeOverride(value);
    }
    if (user && activeCustomerId.startsWith('order_')) {
      const orderId = extractOrderId(activeCustomerId);
      if (orderId) {
        persistOverrideToDb(orderId, value);   // 직렬화 큐 + 저장 커밋 후 reconcile (§16.10)
      }
    }
  }, [user, activeCustomerId, extractOrderId, setShippingFeeOverride, setGuestShippingOverrides, persistOverrideToDb]);

  // 비회원: activeCustomerId별 override를 localStorage에 저장/복원
  useEffect(() => {
    if (user) return;
    if (!activeCustomerId) return;
    const key = `guestShippingOverride-${activeCustomerId}`;
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      try {
        const parsed = JSON.parse(raw);
        setGuestShippingOverrides(prev => ({ ...prev, [activeCustomerId]: parsed }));
        setShippingFeeOverride(parsed);
      } catch { }
    }
  }, [user, activeCustomerId, setShippingFeeOverride, setGuestShippingOverrides]);

  useEffect(() => {
    if (user) return;
    if (!activeCustomerId) return;
    const key = `guestShippingOverride-${activeCustomerId}`;
    const value = guestShippingOverrides[activeCustomerId] ?? null;
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [user, activeCustomerId, guestShippingOverrides]);

  return {
    shippingMode,
    handleShippingModeChange,
    setShippingFeeOverridePersisted,
  };
}
